import React, { useMemo } from 'react';
import type { Maybe } from '@soundxyz/utils';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { useIsSubscribedToArtist } from '@components/basics/SubscribeButton';
import {
  EmailNotificationConfirmationModal,
  EmailNotificationInputModal,
} from '@components/modals/EmailNotificationModal';
import { setFollowingStatus, useFollowingStatus } from '@components/modals/UserStats/store';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalType, useModal } from '@context/modal';
import { useToast } from '@context/toast';
import { RefetchOperationsOnComplete } from '@graphql/helpers/effects';
import {
  EmailVerificationStatus,
  FollowUserDocument,
  GetUserNotificationPreferencesDocument,
  gql,
  IsFollowingDocument,
  IsSubscribedToArtistsNotificationsDocument,
  SetReceiveArtistUpdateEmailsDocument,
  SubscribeToArtistDocument,
  UnsubscribeToArtistDocument,
  useMutation,
  useQuery,
} from '@graphql/reactQuery';
// TODO: https://linear.app/sound/issue/ENG-8462/update-logging-in-usegetnotified
import { useLogError } from '@hooks/useLogError';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY, SPACE } from '@utils/constants';

RefetchOperationsOnComplete({
  trigger: [
    SubscribeToArtistDocument,
    UnsubscribeToArtistDocument,
    FollowUserDocument,
    SetReceiveArtistUpdateEmailsDocument,
  ],
  refetch: [
    IsFollowingDocument,
    IsSubscribedToArtistsNotificationsDocument,
    GetUserNotificationPreferencesDocument,
  ],
});

/*#__PURE__*/ gql(/* GraphQL */ `
  query IsFollowing($input: IsFollowingInput!) {
    isFollowing(input: $input)
  }

  mutation SubscribeToArtist($input: SubscribeArtistActivityNotificationInput!) {
    subscribeArtistActivityNotification(input: $input) {
      __typename
      ... on MutationSubscribeArtistActivityNotificationSuccess {
        data {
          id
          artist {
            id
            name
          }
        }
      }
      ... on CustomerIoAPICallError {
        message
      }
    }
  }

  mutation UnsubscribeToArtist($input: UnsubscribeArtistActivityNotificationInput!) {
    unsubscribeArtistActivityNotification(input: $input) {
      __typename
      ... on MutationUnsubscribeArtistActivityNotificationSuccess {
        data
      }
      ... on CustomerIoAPICallError {
        message
      }
    }
  }
`);

const INBOX_REMINDERS_SUBTEXT = 'to get drop reminders in your inbox.';
export const useGetNotified = ({
  userId,
  artistId,
}: {
  userId: Maybe<string>;
  artistId: Maybe<string>;
}) => {
  const { openModal } = useModal();
  const { openToast, closeToast } = useToast();
  const { isAuthenticatedWithAPI } = useAuth();
  const { login } = useConnectedAuth();
  const logError = useLogError();
  const inboxSubtext = INBOX_REMINDERS_SUBTEXT.split(' ');
  const { mutateAsync: subscribeArtist } = useMutation(SubscribeToArtistDocument, {});
  const { mutateAsync: unsubscribeArtist } = useMutation(UnsubscribeToArtistDocument, {});
  const { mutateAsync: followUser } = useMutation(FollowUserDocument, {});
  const { mutateAsync: toggleReceiveArtistEmails } = useMutation(
    SetReceiveArtistUpdateEmailsDocument,
    {},
  );

  const followingStatus = useFollowingStatus({
    userId,
  });

  const { data, isLoading } = useQuery(GetUserNotificationPreferencesDocument, {
    enabled: isAuthenticatedWithAPI,
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });

  const { isSubscribedToArtist, isSubscribedLoading } = useIsSubscribedToArtist({
    artistId,
  });
  const email = data?.data.userNotificationPreferences?.email;
  const status = data?.data.userNotificationPreferences?.emailVerificationStatus;
  const isFollowingArtist = followingStatus === true;
  const isEmailSet = Boolean(email);
  const needsVerification =
    status === EmailVerificationStatus.NotSet ||
    status === EmailVerificationStatus.Expired ||
    status === EmailVerificationStatus.Pending;

  const showSubscribed = useMemo(() => {
    return isFollowingArtist && isSubscribedToArtist;
  }, [isFollowingArtist, isSubscribedToArtist]);

  const isValidAuth = useStableCallback(() => {
    // Not logged in, opens login modal
    if (!isAuthenticatedWithAPI) {
      login();
      return false;
    }

    closeToast();
    return true;
  });

  const handleEmailVerification = useStableCallback((artistName?: string) => {
    if (!isValidAuth()) return;

    // Email not registered, opens Email Notification Input Modal
    if (!isEmailSet) {
      openModal(ModalType.NOTIFICATIONS_EMAIL_INPUT, {
        body: <EmailNotificationInputModal artistName={!!artistName ? artistName : undefined} />,
      });
      return;
    }

    // Verification expired or pending, open confirmation modal
    if (needsVerification) {
      if (!email) return;

      openModal(ModalType.NOTIFICATIONS_EMAIL_CONFIRMATION, {
        body: <EmailNotificationConfirmationModal email={email} />,
      });
      return;
    }
  });

  const verifyToastContent = (artistName: string) => (
    <span className="inline-flex w-full flex-wrap">
      <button
        className="relative w-fit appearance-none font-medium underline"
        onClick={() => {
          handleEmailVerification(artistName);
        }}
      >
        Verify your email
      </button>
      {SPACE}
      {inboxSubtext.map((word, i) => (
        <span key={`${word}-${i}`}>
          {word}
          {SPACE}
        </span>
      ))}
    </span>
  );

  const handleSubscribe = useStableCallback(async () => {
    if (!isValidAuth() || !artistId || !userId) return;

    // If email has been filled, user follows and is subscribed to artist, handle unsubcribe
    if (isFollowingArtist && isSubscribedToArtist) {
      await unsubscribeArtist(
        {
          input: {
            artists: [artistId],
          },
        },
        {
          onSuccess: ({ data }) => {
            if (
              data.unsubscribeArtistActivityNotification.__typename ===
              'MutationUnsubscribeArtistActivityNotificationSuccess'
            ) {
              openToast({
                text: `You have unsubscribed and won't be notified about new drops.`,
                variant: 'success',
              });
            } else if (
              data.unsubscribeArtistActivityNotification.__typename === 'CustomerIoAPICallError'
            ) {
              logError({
                error: new Error(data.unsubscribeArtistActivityNotification.message),
                toast: data.unsubscribeArtistActivityNotification.message,
              });
            }
          },
        },
      ).catch(err => {
        logError({
          error: err,
          toast: 'Error unsubscribing to artist. Please try again',
        });
      });
      return;
    }

    // If user is following artist but is not subscribed, handle subscribe
    if (isFollowingArtist && !isSubscribedToArtist) {
      await subscribeArtist(
        {
          input: {
            artist: artistId,
          },
        },
        {
          onSuccess: ({ data }) => {
            if (
              data.subscribeArtistActivityNotification.__typename ===
              'MutationSubscribeArtistActivityNotificationSuccess'
            ) {
              const artistName = data.subscribeArtistActivityNotification.data.artist.name;
              openToast({
                text: (
                  <div className="inline-flex w-full flex-wrap break-words">
                    You have subscribed to{SPACE}
                    {artistName}.{SPACE}
                    {needsVerification
                      ? verifyToastContent(artistName)
                      : "You'll get notified about their new drops."}
                  </div>
                ),
                variant: 'success',
              });
              toggleReceiveArtistEmails({
                enable: true,
              });
            } else if (
              data.subscribeArtistActivityNotification.__typename === 'CustomerIoAPICallError'
            ) {
              logError({
                error: new Error(data.subscribeArtistActivityNotification.message),
                toast: data.subscribeArtistActivityNotification.message,
              });
            }
          },
        },
      ).catch(err => {
        logError({
          error: err,
          toast: 'Error subscribing to artist. Please try again',
        });
      });
      return;
    }

    // If user is not following nor subscribed to artist, handle subscribe + follow
    if (!isFollowingArtist && !isSubscribedToArtist) {
      setFollowingStatus({
        userId,
        isFollowing: true,
      });
      await Promise.all([
        subscribeArtist({
          input: {
            artist: artistId,
          },
        }),
        followUser({ input: { user: userId } }),
        toggleReceiveArtistEmails({
          enable: true,
        }),
      ])
        .then(data => {
          const subscribeArtistActivityNotification =
            data[0].data.subscribeArtistActivityNotification;
          if (
            subscribeArtistActivityNotification.__typename ===
            'MutationSubscribeArtistActivityNotificationSuccess'
          ) {
            const artistName = subscribeArtistActivityNotification.data.artist.name;
            openToast({
              text: (
                <div className="inline-flex w-full flex-wrap break-words">
                  You have subscribed and followed{SPACE}
                  {artistName}.{SPACE}
                  {needsVerification
                    ? verifyToastContent(artistName)
                    : "You'll get notified about their new drops."}
                </div>
              ),
              variant: 'success',
            });
          } else if (subscribeArtistActivityNotification.__typename === 'CustomerIoAPICallError') {
            setFollowingStatus({
              userId,
              isFollowing: false,
            });
            logError({
              error: new Error(subscribeArtistActivityNotification.message),
              toast: subscribeArtistActivityNotification.message,
            });
          }
        })
        .catch(err => {
          setFollowingStatus({
            userId,
            isFollowing: false,
          });
          logError({ error: err, toast: 'Error subscribing and following user.' });
        });
      return;
    }
  });

  const handleClick = useStableCallback(() => {
    handleSubscribe();
  });

  const loading =
    followingStatus === 'loading' || isLoading || isSubscribedLoading || !artistId || !userId;

  return {
    handleClick,
    handleSubscribe,
    handleEmailVerification,
    loading,
    showSubscribed,
    isSubscribedToArtist,
    isEmailSet,
    needsVerification,
  };
};
